// ═══════════════════════════════════════════════════════════════
// GIVING LEVELS PANEL
// Path: src/components/settings/GivingLevelsPanel.jsx
//
// Settings → Giving Levels tab. Configures the donor recognition
// tiers stored in chapter_settings.giving_levels (JSONB array).
//
// Two scopes:
//   - perpetuity: matched against lifetime cumulative giving;
//     once earned, the donor stays at this tier forever.
//   - annual:     matched against annual giving for a chosen
//     window; recalculated each cycle.
//
// Tier shape (per row in the JSONB array):
//   {
//     id:         string (uuid, stable React key)
//     scope:      "perpetuity" | "annual"
//     name:       string  (e.g. "Angels", "Benefactors")
//     min_amount: number  (required, dollars)
//     max_amount: number | null  (null = no upper bound)
//     sort_order: number  (descending — highest tier listed first)
//   }
//
// All edits flow through setChapterSettings, which AppContext
// auto-saves to Supabase. No explicit save button.
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { Award, Plus, Trash2, Infinity as InfinityIcon } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Card } from "../ui/Card";

// ── Helpers ──────────────────────────────────────────────────────

const newTier = (scope) => ({
  id:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `tier_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  scope,
  name: "",
  min_amount: 0,
  max_amount: null,
  sort_order: 0,
});

const formatCurrency = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const num = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(num)) return "";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Sort tiers descending by min_amount so the highest tier
// appears at the top of the editor (natural donor-wall order).
const sortDescending = (a, b) => (b.min_amount || 0) - (a.min_amount || 0);

// ── Tier Row ─────────────────────────────────────────────────────

const TierRow = ({ tier, palette, onChange, onRemove }) => {
  const inputStyle = {
    padding: "8px 10px",
    borderRadius: 6,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
    boxSizing: "border-box",
    width: "100%",
  };

  const cellLabel = {
    fontSize: 10,
    fontWeight: 700,
    color: palette.text3,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 3,
  };

  const hasNoMax =
    tier.max_amount === null ||
    tier.max_amount === undefined ||
    tier.max_amount === "";

  const isInvalid = !tier.name?.trim() || tier.min_amount === "" || tier.min_amount === null || tier.min_amount === undefined;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr auto",
        gap: 10,
        alignItems: "end",
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${
          isInvalid ? "#fca5a5" : palette.cardBorder
        }`,
        background: isInvalid ? "#fef2f2" : palette.inputBg,
        marginBottom: 8,
      }}
    >
      {/* Name */}
      <div>
        <div style={cellLabel}>Tier Name</div>
        <input
          type="text"
          value={tier.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g. Angels"
          style={inputStyle}
        />
      </div>

      {/* Min */}
      <div>
        <div style={cellLabel}>Minimum ($)</div>
        <input
          type="number"
          min="0"
          step="1"
          value={
            tier.min_amount === null || tier.min_amount === undefined
              ? ""
              : tier.min_amount
          }
          onChange={(e) => {
            const v = e.target.value;
            onChange({ min_amount: v === "" ? 0 : parseFloat(v) });
          }}
          placeholder="0"
          style={inputStyle}
        />
      </div>

      {/* Max */}
      <div>
        <div style={cellLabel}>Maximum ($)</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="number"
            min="0"
            step="1"
            value={hasNoMax ? "" : tier.max_amount}
            disabled={hasNoMax}
            onChange={(e) => {
              const v = e.target.value;
              onChange({ max_amount: v === "" ? null : parseFloat(v) });
            }}
            placeholder="no max"
            style={{
              ...inputStyle,
              opacity: hasNoMax ? 0.5 : 1,
            }}
          />
          <label
            title="No upper bound — top tier"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: palette.text3,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={hasNoMax}
              onChange={(e) =>
                onChange({
                  max_amount: e.target.checked ? null : tier.min_amount + 1,
                })
              }
              style={{ cursor: "pointer" }}
            />
            <InfinityIcon size={ICON_SIZES.xs} /> none
          </label>
        </div>
      </div>

      {/* Remove */}
      <button
        onClick={onRemove}
        title="Remove tier"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#ef4444",
          padding: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Trash2 size={ICON_SIZES.sm} />
      </button>
    </div>
  );
};

// ── Tier Section (Perpetuity or Annual) ──────────────────────────

const TierSection = ({
  scope,
  title,
  description,
  tiers,
  palette,
  onChangeTier,
  onAddTier,
  onRemoveTier,
}) => {
  const sorted = [...tiers].sort(sortDescending);

  return (
    <Card style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <Award size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
        <span
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 600,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </span>
      </div>
      <p
        style={{
          fontSize: FONT_SIZES.xs,
          color: palette.text2,
          marginTop: 0,
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        {description}
      </p>

      {sorted.length === 0 ? (
        <div
          style={{
            padding: "16px 14px",
            borderRadius: 8,
            border: `1px dashed ${palette.cardBorder}`,
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            textAlign: "center",
            marginBottom: 12,
          }}
        >
          No tiers configured yet. Click "Add tier" to create one.
        </div>
      ) : (
        <div style={{ marginBottom: 8 }}>
          {sorted.map((tier) => (
            <TierRow
              key={tier.id}
              tier={tier}
              palette={palette}
              onChange={(patch) => onChangeTier(tier.id, patch)}
              onRemove={() => onRemoveTier(tier.id)}
            />
          ))}
        </div>
      )}

      {/* Live preview line: highest → lowest summary */}
      {sorted.length > 0 && (
        <div
          style={{
            fontSize: 11,
            color: palette.text3,
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          Configured:{" "}
          {sorted
            .filter((t) => t.name?.trim())
            .map((t) => {
              const min = formatCurrency(t.min_amount || 0);
              const max =
                t.max_amount === null || t.max_amount === undefined
                  ? "+"
                  : `–${formatCurrency(t.max_amount)}`;
              return `${t.name} (${min}${max})`;
            })
            .join("  •  ")}
        </div>
      )}

      <button
        onClick={() => onAddTier(scope)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 14px",
          borderRadius: 7,
          border: `1px solid ${palette.accent1}`,
          background: `${palette.accent1}10`,
          color: palette.accent1,
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          fontFamily: FONT_FAMILY,
          cursor: "pointer",
        }}
      >
        <Plus size={ICON_SIZES.xs} /> Add tier
      </button>
    </Card>
  );
};

// ── Main Panel ───────────────────────────────────────────────────

export const GivingLevelsPanel = ({ palette }) => {
  const { chapterSettings, setChapterSettings } = useApp();

  const allTiers = Array.isArray(chapterSettings.givingLevels)
    ? chapterSettings.givingLevels
    : [];

  // Treat any tier missing a scope as "perpetuity" (back-compat
  // with any tiers configured before this UI existed).
  const perpetuityTiers = allTiers.filter(
    (t) => (t.scope || "perpetuity") === "perpetuity"
  );
  const annualTiers = allTiers.filter((t) => t.scope === "annual");

  const writeAll = (next) => {
    setChapterSettings((prev) => ({ ...prev, givingLevels: next }));
  };

  const handleAddTier = (scope) => {
    writeAll([...allTiers, newTier(scope)]);
  };

  const handleChangeTier = (id, patch) => {
    writeAll(
      allTiers.map((t) =>
        t.id === id ? { ...t, ...patch } : t
      )
    );
  };

  const handleRemoveTier = (id) => {
    writeAll(allTiers.filter((t) => t.id !== id));
  };

  return (
    <>
      {/* Intro card */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <Award size={ICON_SIZES.lg} style={{ color: palette.accent1 }} />
          <div
            style={{
              fontSize: FONT_SIZES.base,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            Donor Recognition Tiers
          </div>
        </div>
        <p
          style={{
            color: palette.text2,
            fontSize: FONT_SIZES.sm,
            margin: "0 0 4px 0",
            lineHeight: 1.5,
          }}
        >
          Configure the giving levels used on your donor wall and program
          recognition lists. Define two kinds of tiers:
        </p>
        <ul
          style={{
            color: palette.text2,
            fontSize: FONT_SIZES.sm,
            lineHeight: 1.6,
            marginTop: 6,
            marginBottom: 0,
            paddingLeft: 22,
          }}
        >
          <li>
            <strong>Perpetuity tiers</strong> — earned by lifetime cumulative
            giving (e.g. Angels at $100,000+). Once earned, the donor remains
            at this tier forever.
          </li>
          <li>
            <strong>Annual tiers</strong> — earned by giving in a chosen window
            (e.g. Benefactors at $100–$499 per year). Recalculated each cycle.
          </li>
        </ul>
      </Card>

      <TierSection
        scope="perpetuity"
        title="Lifetime Recognition Tiers (Perpetuity)"
        description="Top of the donor wall — donors qualify based on cumulative lifetime giving and remain recognized in perpetuity. Leave Maximum blank on the highest tier (it has no upper bound)."
        tiers={perpetuityTiers}
        palette={palette}
        onChangeTier={handleChangeTier}
        onAddTier={handleAddTier}
        onRemoveTier={handleRemoveTier}
      />

      <TierSection
        scope="annual"
        title="Annual Giving Levels"
        description="Donors qualify each cycle based on giving inside the chosen reporting window. Use these for the standard donor-wall list refreshed twice a year."
        tiers={annualTiers}
        palette={palette}
        onChangeTier={handleChangeTier}
        onAddTier={handleAddTier}
        onRemoveTier={handleRemoveTier}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "10px 14px",
          borderRadius: 8,
          background: `${palette.accent1}06`,
          border: `1px solid ${palette.accent1}15`,
          fontSize: FONT_SIZES.xs,
          color: palette.text2,
          lineHeight: 1.5,
        }}
      >
        Changes auto-save to Supabase. The Donor Recognition report and donor
        cards will pick up new tier definitions immediately.
      </div>
    </>
  );
};
